import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar as CalendarIcon, ArrowRight } from 'lucide-react';
import { collectiveReadings } from '../data/mock';

export const CollectiveDetailPage = () => {
  const { slug } = useParams();
  const reading = collectiveReadings.find(r => r.slug === slug);
  const currentIndex = collectiveReadings.findIndex(r => r.slug === slug);
  const prevReading = collectiveReadings[currentIndex + 1];
  const nextReading = collectiveReadings[currentIndex - 1];

  if (!reading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-charcoal)]">
        <div className="text-center">
          <h1 className="font-serif text-4xl mb-4">Reading Not Found</h1>
          <Link to="/collective" className="text-[var(--color-gold)] hover:underline">
            Return to Collective
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[var(--color-charcoal)]">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center pt-32 pb-20">
        <div className="absolute inset-0">
          <img
            src={reading.image}
            alt=""
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-charcoal)] via-[var(--color-charcoal)]/70 to-[var(--color-charcoal)]/50" />
        </div>
        
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <Link
              to="/collective"
              className="inline-flex items-center gap-2 text-[var(--color-gold)] hover:text-[var(--color-gold-light)] transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Collective
            </Link>
            
            <div className="flex items-center gap-2 text-[var(--color-gold)] mb-4">
              <CalendarIcon className="w-4 h-4" />
              <span className="text-sm">{reading.month}</span>
            </div>
            
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6 leading-tight">
              {reading.title}
            </h1>
            
            <p className="text-xl text-[var(--color-cream)]/70 leading-relaxed">
              {reading.excerpt}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="prose prose-lg prose-invert max-w-none"
          >
            <p className="text-lg text-[var(--color-cream)]/80 leading-relaxed mb-8">
              {reading.content}
            </p>
            
            {/* Placeholder content for full reading */}
            <div className="space-y-6 text-[var(--color-cream)]/70">
              <p className="leading-relaxed">
                The cards this month speak to the liminal spaces—those threshold moments where we stand between what was and what is becoming. There is wisdom in pausing here, in resisting the urge to rush through uncertainty toward false clarity.
              </p>
              
              <p className="leading-relaxed">
                What wants to be released? What has served its purpose and now asks for your gratitude as it makes its way out? These are not easy questions, but they are necessary ones. The collective energy supports this kind of honest accounting.
              </p>
              
              <div className="my-12 p-8 bg-[var(--color-charcoal-light)] rounded-lg border-l-4 border-[var(--color-gold)]">
                <p className="font-serif text-xl text-[var(--color-cream)] italic">
                  "The threshold is not a place of failure. It is a place of becoming."
                </p>
              </div>
              
              <p className="leading-relaxed">
                As we move through these weeks, I invite you to hold your questions loosely. Not everything needs an answer right now. Sometimes the most powerful practice is simply to sit with what is—to witness your own experience without rushing to fix, explain, or transcend it.
              </p>
              
              <p className="leading-relaxed">
                The cards remind us that transformation is rarely dramatic. More often, it is quiet—a gradual softening, a slow turning toward what matters. Trust the pace of your own becoming.
              </p>
            </div>
          </motion.div>

          {/* Navigation */}
          <div className="mt-16 pt-8 border-t border-[var(--color-dark-purple)]/30">
            <div className="flex justify-between items-center">
              {prevReading ? (
                <Link
                  to={`/collective/${prevReading.slug}`}
                  className="flex items-center gap-3 text-[var(--color-cream)]/60 hover:text-[var(--color-gold)] transition-colors group"
                >
                  <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                  <div className="text-left">
                    <span className="text-xs uppercase tracking-wider">Previous</span>
                    <p className="font-serif">{prevReading.title}</p>
                  </div>
                </Link>
              ) : (
                <div />
              )}
              
              {nextReading ? (
                <Link
                  to={`/collective/${nextReading.slug}`}
                  className="flex items-center gap-3 text-[var(--color-cream)]/60 hover:text-[var(--color-gold)] transition-colors group"
                >
                  <div className="text-right">
                    <span className="text-xs uppercase tracking-wider">Next</span>
                    <p className="font-serif">{nextReading.title}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              ) : (
                <div />
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
